// iOS and Android block audio autoplay until the user interacts with the page.
// This utility unlocks audio on the very first gesture so all subsequent
// programmatic plays (autoplay) work seamlessly without requiring a tap.

let isUnlocked = false;
const pendingCallbacks = [];

const triggerUnlock = () => {
	if (isUnlocked) return;

	// Mark unlocked synchronously — we are inside a gesture handler.
	// Setting this before anything async ensures onAudioUnlocked callbacks
	// fire in the same call stack rather than in a later microtask/macrotask.
	isUnlocked = true;

	// AudioContext unlock — resumes the context so Web Audio API works.
	// On iOS, this also signals to the browser that the page intends to play
	// audio, which improves HTMLAudioElement autoplay reliability.
	const AudioCtx = window.AudioContext || window.webkitAudioContext;
	if (AudioCtx) {
		try {
			const ctx = new AudioCtx();
			const buf = ctx.createBuffer(1, 1, 22050);
			const src = ctx.createBufferSource();
			src.buffer = buf;
			src.connect(ctx.destination);
			src.start(0);
			ctx.resume();
		} catch (e) {}
	}

	// NOTE: We intentionally do NOT play a silent HTMLAudioElement here.
	// On iOS, calling silent.play() inside the gesture handler consumes the
	// user-activation token, leaving nothing for the actual AudioPlayer
	// elements whose play() calls fire moments later in componentDidMount.
	// The AudioContext unlock above is sufficient to register user intent.

	pendingCallbacks.forEach((cb) => cb());
	pendingCallbacks.length = 0;
};

// Call this once at app root. Listens for the first user gesture and unlocks.
export const setupAudioUnlock = () => {
	if (isUnlocked) return;

	const events = ['touchstart', 'mousedown', 'keydown'];

	const handler = () => {
		triggerUnlock();
		events.forEach((e) =>
			document.removeEventListener(e, handler)
		);
	};

	events.forEach((e) =>
		document.addEventListener(e, handler, {passive: true})
	);
};

// AudioPlayer calls this when autoplay is blocked.
// The callback fires immediately if already unlocked,
// or as soon as the next user gesture unlocks audio.
export const onAudioUnlocked = (cb) => {
	if (isUnlocked) {
		cb();
	} else {
		pendingCallbacks.push(cb);
	}
};

export const isAudioUnlocked = () => isUnlocked;

// Call directly inside a user-gesture handler (e.g. a button onClick)
// to explicitly trigger unlock without waiting for the document listener.
export const unlockAudioNow = () => triggerUnlock();
